import { useTheme } from "@mui/material";
import { SxProps, Theme } from "@mui/system";
import {
  Automatic20Px,
  Hold20Px,
  ManualPick20Px,
  ManualPutaway20Px,
  Search20Px
} from "@qubit/autoparts";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";
import FullPageLayout from "~/components/layout/FullPageLayout";
import { MainButton } from "~/features/autostoreMain/MainButton";
import { useFcNavigate } from "~/hooks/useFcNavigate";
import { useNavbar, ViewNameTranslation } from "~/hooks/useNavbar";
import { mixpanelTrack, useMixpanelPageName } from "~/lib/mixpanel-tracking";

type Props = { viewTitle?: ViewNameTranslation };

const manualOpsButtonSx: SxProps<Theme> = {
  height: "100%",
  maxHeight: 420
};

export function ManualOps({ viewTitle }: Props) {
  const { t } = useTranslation();
  const navigate = useFcNavigate();
  const theme = useTheme();
  const trackedPageName = useMixpanelPageName();

  const showPickButton = useAppSelector(
    (state) =>
      !state.store.usersFulfillmentCenter?.pickingConfigurations ||
      state.store.usersFulfillmentCenter.pickingConfigurations.includes(
        "Manual"
      )
  );

  const showShipButton = useAppSelector(
    (state) =>
      !state.store.usersFulfillmentCenter?.waveCompletionConfiguration ||
      state.store.usersFulfillmentCenter.waveCompletionConfiguration ===
        "TotesVerified"
  );

  useNavbar({ viewTitle });

  return (
    <FullPageLayout
      gridTemplateColumns={{
        xs: "repeat(2, 1fr)",
        sm: "repeat(3, minmax(200px, 400px))"
      }}
      sx={{
        gridAutoRows: {
          xs: "minmax(100px, 1fr)",
          sm: "minmax(150px, 1fr)",
          md: "minmax(200px, 420px)"
        },
        gap: {
          xs: 1,
          md: 2
        },
        padding: {
          xs: 1,
          md: 2,
          lg: 3
        },
        justifyContent: "center"
      }}
    >
      <MainButton
        onClick={(): void => {
          mixpanelTrack({
            trackedPageName,
            type: "Button Click",
            label: "Putaway"
          });
          navigate(`/putaway`);
        }}
        sx={manualOpsButtonSx}
        icon={
          <ManualPutaway20Px
            fill={theme.palette.primary.contrastText}
            width={30}
            height={30}
          />
        }
      >
        {t("putaway")}
      </MainButton>

      <MainButton
        onClick={(): void => {
          mixpanelTrack({
            trackedPageName,
            type: "Button Click",
            label: "Inventory Lookup"
          });
          navigate(`/manual-inventory/product`);
        }}
        sx={manualOpsButtonSx}
        icon={
          <Search20Px
            fill={theme.palette.primary.contrastText}
            width={30}
            height={30}
          />
        }
      >
        {t("inventory lookup")}
      </MainButton>

      <MainButton
        className="home_button_inventory"
        onClick={(): void => {
          mixpanelTrack({
            trackedPageName,
            type: "Button Click",
            label: "Inventory Holds"
          });
          navigate(`/inventory-holds`);
        }}
        sx={manualOpsButtonSx}
        icon={
          <Hold20Px
            fill={theme.palette.primary.contrastText}
            width={30}
            height={30}
          />
        }
      >
        {t("nav.link.inventory holds")}
      </MainButton>

      <MainButton
        onClick={(): void => {
          mixpanelTrack({
            trackedPageName,
            type: "Button Click",
            label: "Cycle Counts"
          });
          navigate(`/cycle-count`);
        }}
        sx={manualOpsButtonSx}
        icon={
          <Automatic20Px
            fill={theme.palette.primary.contrastText}
            width={30}
            height={30}
          />
        }
      >
        {t("nav.viewname.cycle counts")}
      </MainButton>

      {showPickButton && (
        <MainButton
          onClick={(): void => {
            mixpanelTrack({
              trackedPageName,
              type: "Button Click",
              label: "Picking"
            });
            navigate(`/pick-batches`);
          }}
          sx={manualOpsButtonSx}
          icon={
            <ManualPick20Px
              fill={theme.palette.primary.contrastText}
              width={30}
              height={30}
            />
          }
        >
          {t("nav.link.picking")}
        </MainButton>
      )}

      {showShipButton && (
        <MainButton
          onClick={(): void => {
            mixpanelTrack({
              trackedPageName,
              type: "Button Click",
              label: "Order Consolidation"
            });
            navigate(`/ship/consolidation`);
          }}
          sx={manualOpsButtonSx}
        >
          {t("nav.link.order consolidation")}
        </MainButton>
      )}
    </FullPageLayout>
  );
}
